// src/hooks/useObjectActions.js
import { useCallback } from "react";
import { actualizarObjeto, togglePerdido } from "../../services/api";
import { toast } from "react-toastify";

export function useObjectActions(setObjetos) {
  // Función para editar (actualizar) un objeto
  // NOTA: ObjectEditModal ya guarda los datos en el servidor, aquí solo actualizamos el estado local
  const submitEditObject = useCallback(
    (updatedData) => {
      // ✅ Actualizar estado local con los datos completos (incluye fotoUrl con cache-busting)
      // No hacemos llamada API aquí porque ObjectEditModal ya la hizo
      setObjetos((prev) =>
        prev.map((o) => (o.id === updatedData.id ? { ...o, ...updatedData } : o))
      );
      return true;
    },
    [setObjetos]
  );

  // Función para marcar objeto como perdido o encontrado
  const reportLostOrFound = useCallback(
    async (obj, { wantReward, markFound } = { wantReward: false, markFound: false }) => {
      if (obj.extraviado) {
        // Estaba perdido → marcar como encontrado (SIN confirmación)
        try {
          const resp = await togglePerdido(obj.id);
          if (resp.error) {
            return { success: false, error: resp.error };
          }
          setObjetos((prev) =>
            prev.map((o) => (o.id === obj.id ? { ...o, extraviado: resp.extraviado } : o))
          );
          return { success: true, action: 'found' };
        } catch (err) {
          return { success: false, error: "No se pudo cambiar el estado perdido/encontrado." };
        }
      } else {
        // No estaba perdido → marcar como perdido
        if (!wantReward) {
          // Usuario dijo NO a recompensa - marcar perdido SIN ofrecer recompensa
          try {
            const camposActualizar = {
              extraviado: true,
              ofrecerRecompensa: false,  // Explícitamente NO ofrecer recompensa
            };
            const resp = await actualizarObjeto(obj.id, camposActualizar);
            if (resp.error) {
              return { success: false, error: resp.error };
            }
            setObjetos((prev) =>
              prev.map((o) => (o.id === obj.id ? { ...o, ...camposActualizar } : o))
            );
            return { success: true, action: 'lost' };
          } catch (err) {
            return { success: false, error: "No se pudo cambiar el estado a perdido." };
          }
        } else {
          // Si se quiere ofrecer recompensa, retornar para mostrar formulario
          return { success: null, showRewardForm: true };
        }
      }
    },
    [setObjetos]
  );

  // Función para enviar los datos del formulario de "Objeto perdido con recompensa"
  const submitLostItem = useCallback(
    async (objToMarkLost, rewardData) => {
      if (!objToMarkLost) {
        toast.error("❌ No se pudo identificar el objeto");
        return false;
      }

      // Solo enviar los campos que queremos actualizar
      const camposActualizar = {
        extraviado: true,
        ofrecerRecompensa: true,  // Usuario eligió SÍ ofrecer recompensa
        recompensa: rewardData.recompensaOfrecida,
      };

      try {
        const resp = await actualizarObjeto(objToMarkLost.id, camposActualizar);
        if (resp.error) {
          toast.error("❌ Error al marcar perdido: " + resp.error);
          return false;
        }

        // Actualizar estado local con los campos actualizados
        setObjetos((prev) =>
          prev.map((o) => (o.id === objToMarkLost.id ? { ...o, ...camposActualizar } : o))
        );
        return true;
      } catch (err) {
        console.error("Error en submitLostItem:", err);
        toast.error("❌ No se pudo actualizar el objeto");
        return false;
      }
    },
    [setObjetos]
  );

  // Función para activar el perfil público de búsqueda con datos adicionales
  const submitPublicProfile = useCallback(
    async (obj, profileData) => {
      if (!obj) {
        toast.error("❌ No se pudo identificar el objeto");
        return false;
      }

      console.log('[submitPublicProfile] profileData recibido:', JSON.stringify(profileData, null, 2));

      const camposActualizar = {
        perfilPublicoBusqueda: true,
        fechaPerdida: profileData.fechaPerdida || "",
        queLlevabaPuesto: profileData.queLlevabaPuesto || "",
        dondeBuscar: profileData.dondeBuscar || "",
        queProvocoEscape: profileData.queProvocoEscape || "",
      };

      // Si viene ubicación, incluirla también
      if (profileData.ubicacionPerdida) {
        camposActualizar.ubicacionPerdida = profileData.ubicacionPerdida;
      }

      console.log('[submitPublicProfile] Payload PATCH:', JSON.stringify(camposActualizar, null, 2));

      try {
        const resp = await actualizarObjeto(obj.id, camposActualizar);
        if (resp.error) {
          toast.error("❌ Error al activar perfil público: " + resp.error);
          return false;
        }

        // Incluir slug generado por el servidor en el estado local
        const slugFromServer = resp.data?.perfilPublicoSlug;
        setObjetos((prev) =>
          prev.map((o) => (o.id === obj.id ? {
            ...o,
            ...camposActualizar,
            ...(slugFromServer ? { perfilPublicoSlug: slugFromServer } : {})
          } : o))
        );
        return true;
      } catch (err) {
        console.error("Error en submitPublicProfile:", err);
        toast.error("❌ No se pudo activar el perfil público");
        return false;
      }
    },
    [setObjetos]
  );

  // Función para cambiar el estado de bloqueo de un objeto
 const toggleLock = useCallback(
  async (obj, confirmed = false) => {
    const estabaBloqueado = obj.bloqueado === true;
    
    // Si no está confirmado, retornar para mostrar el modal
    if (!confirmed) {
      return { 
        success: null, 
        showLockModal: true,
        isBlocked: estabaBloqueado 
      };
    }

    // Si está confirmado, proceder con el bloqueo/desbloqueo
    try {
      const nuevoEstadoBloqueo = !estabaBloqueado;

      const resp = await actualizarObjeto(obj.id, { 
        bloqueado: nuevoEstadoBloqueo 
      });
      
      if (resp.error) {
        toast.error("❌ Error al cambiar estado de bloqueo: " + resp.error);
        return { success: false };
      }
      
      // Actualizar el estado local
      setObjetos((prev) =>
        prev.map((o) => (o.id === obj.id ? { ...o, bloqueado: nuevoEstadoBloqueo } : o))
      );
      
      if (nuevoEstadoBloqueo) {
        toast.success("🔒 Objeto bloqueado con éxito");
      } else {
        toast.success("🔓 Objeto desbloqueado con éxito");
      }
      return { success: true };
    } catch (err) {
      console.error("Error en bloquear/desbloquear =>", err);
      toast.error("❌ No se pudo cambiar el estado de bloqueo");
      return { success: false };
    }
  },
  [setObjetos]
);

  // Función para solicitar el desbloqueo de un objeto
  const requestUnlock = useCallback(
    async (obj) => {
      try {
        // Solo enviar el campo que necesitamos actualizar (no todo el objeto)
        const resp = await actualizarObjeto(obj.id, { bloqueado: false });
        if (resp.error) {
          toast.error("❌ Error al solicitar desbloqueo: " + resp.error);
          return false;
        }
        setObjetos((prev) =>
          prev.map((o) => (o.id === obj.id ? { ...o, bloqueado: false } : o))
        );
        toast.info("🔧 Solicitud de desbloqueo enviada");
        return true;
      } catch (err) {
        console.error("Error solicitando desbloqueo =>", err);
        toast.error("❌ No se pudo solicitar el desbloqueo");
        return false;
      }
    },
    [setObjetos]
  );

  return { submitEditObject, reportLostOrFound, submitLostItem, submitPublicProfile, toggleLock, requestUnlock };
}